// Map our internal symbols to TradingView symbols.
// For unprefixed US stocks we intentionally return the bare symbol so
// TradingView's own search resolves the right exchange (NASDAQ vs NYSE vs AMEX).
// Hardcoding NASDAQ:JPM (NYSE-listed) used to silently break the chart.
export function toTradingViewSymbol(sym) {
  if (!sym) return "AAPL";
  const s = sym.toUpperCase();
  // crypto BTC-USD -> BINANCE:BTCUSDT
  if (s.endsWith("-USD")) {
    const base = s.replace("-USD", "");
    return `BINANCE:${base}USDT`;
  }
  // forex EURUSD=X -> FX_IDC:EURUSD
  if (s.endsWith("=X")) {
    return `FX_IDC:${s.replace("=X", "")}`;
  }
  // futures GC=F (commodities) -> COMEX:GC1!
  if (s.endsWith("=F")) {
    const root = s.replace("=F", "");
    const mapping = { GC: "COMEX:GC1!", SI: "COMEX:SI1!", CL: "NYMEX:CL1!", NG: "NYMEX:NG1!", HG: "COMEX:HG1!" };
    return mapping[root] || `COMEX:${root}1!`;
  }
  // Treasury yield indices on yfinance start with ^
  const yieldMap = {
    "^TNX": "TVC:US10Y",
    "^TYX": "TVC:US30Y",
    "^FVX": "TVC:US05Y",
    "^IRX": "TVC:US03MY",
  };
  if (yieldMap[s]) return yieldMap[s];
  // Indices — use TradingView FREE feeds (FOREXCOM/TVC/CAPITALCOM), NOT paid SP:/DJ: feeds.
  const indexMap = {
    "^GSPC": "FOREXCOM:SPXUSD",      // free
    "^IXIC": "NASDAQ:IXIC",          // free
    "^DJI": "FOREXCOM:DJIUSD",       // free
    "^RUT": "FOREXCOM:RUTUSD",       // free
    "^NSEI": "NSE:NIFTY",
    "^BSESN": "BSE:SENSEX",
    "^FTSE": "TVC:UKX",
    "^GDAXI": "TVC:DEU40",
    "^FCHI": "TVC:CAC40",
    "^STOXX50E": "TVC:SX5E",
    "^N225": "TVC:NI225",
    "^HSI": "TVC:HSI",
    "^KS11": "TVC:KOSPI",
    "^TWII": "TVC:TWII",
    "^AXJO": "ASX:XJO",
    "^STI": "TVC:STI",
    "^VIX": "TVC:VIX",
  };
  if (indexMap[s]) return indexMap[s];
  // India — use BSE: prefix on the embed because TradingView's free widget
  // rejects most `NSE:` symbols with "This symbol is only available on
  // TradingView". BSE: serves the same companies and renders on D/W/M intervals.
  if (s.endsWith(".NS")) return `BSE:${s.replace(".NS", "")}`;
  if (s.endsWith(".BO")) return `BSE:${s.replace(".BO", "")}`;
  // Europe
  if (s.endsWith(".AS")) return `EURONEXT:${s.replace(".AS", "")}`;
  if (s.endsWith(".PA")) return `EURONEXT:${s.replace(".PA", "")}`;
  if (s.endsWith(".BR")) return `EURONEXT:${s.replace(".BR", "")}`;
  if (s.endsWith(".LS")) return `EURONEXT:${s.replace(".LS", "")}`;
  if (s.endsWith(".DE")) return `XETR:${s.replace(".DE", "")}`;
  if (s.endsWith(".F"))  return `FWB:${s.replace(".F", "")}`;
  if (s.endsWith(".MI")) return `MIL:${s.replace(".MI", "")}`;
  if (s.endsWith(".MC")) return `BME:${s.replace(".MC", "")}`;
  if (s.endsWith(".SW")) return `SIX:${s.replace(".SW", "")}`;
  if (s.endsWith(".L"))  return `LSE:${s.replace(".L", "")}`;
  // Americas (non-US)
  if (s.endsWith(".TO")) return `TSX:${s.replace(".TO", "")}`;
  if (s.endsWith(".V"))  return `TSXV:${s.replace(".V", "")}`;
  if (s.endsWith(".SA")) return `BMFBOVESPA:${s.replace(".SA", "")}`;
  if (s.endsWith(".MX")) return `BMV:${s.replace(".MX", "")}`;
  // Asia / Pacific
  if (s.endsWith(".T"))  return `TSE:${s.replace(".T", "")}`;
  if (s.endsWith(".HK")) return `HKEX:${s.replace(".HK", "").replace(/^0+/, "")}`;
  if (s.endsWith(".KS")) return `KRX:${s.replace(".KS", "")}`;
  if (s.endsWith(".SS")) return `SSE:${s.replace(".SS", "")}`;
  if (s.endsWith(".SZ")) return `SZSE:${s.replace(".SZ", "")}`;
  if (s.endsWith(".TW")) return `TWSE:${s.replace(".TW", "")}`;
  if (s.endsWith(".AX")) return `ASX:${s.replace(".AX", "")}`;
  if (s.endsWith(".SI")) return `SGX:${s.replace(".SI", "")}`;

  // Plain US ticker — return BARE so TradingView's search picks NYSE vs NASDAQ vs AMEX.
  // (Hardcoding NASDAQ: silently broke many NYSE-listed names like JPM/V/JNJ.)
  return s;
}

// Some TradingView feeds (Indian BSE, certain emerging-market exchanges) only
// expose Daily/Weekly/Monthly bars on the free embed. Return the correct default.
export function toTradingViewInterval(sym) {
  if (!sym) return "60";
  const s = sym.toUpperCase();
  // Indian stocks on BSE: feed → daily only
  if (s.endsWith(".NS") || s.endsWith(".BO")) return "D";
  // Some Asian listings are also daily-only on the free embed
  if (s.endsWith(".KS") || s.endsWith(".TW") || s.endsWith(".SS") || s.endsWith(".SZ")) return "D";
  return "60";
}
